/**
 * ElevenLabs (v2) interview engine — candidate-side service layer (#2810).
 *
 * All endpoints live under `/interviews/el/*` and use the same standard envelope
 * (`{ code, message, data, errors, meta }`) and the same candidate auth as the
 * rest of the interview flow (`authToken` for logged-in, `tempAuthToken` for the
 * no-login candidate page). Each helper unwraps and returns the `data` payload
 * (or `null` on failure) so the component can stay declarative.
 *
 * Contract: see GitHub issue #2810 (BE already implemented + deployed behind the
 * per-company `interview_engine` flag).
 */
import axios from "axios";

import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { isClientError, StandardResponse } from "@service/interview";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/** Candidate auth — logged-in token first, then the no-login candidate token. */
function authHeaders() {
    const token = getCookie("authToken") || getCookie("tempAuthToken");
    // Omit the Authorization header entirely when no token exists, rather than
    // sending `Bearer undefined` (which masks auth failures and can trip backend
    // header validation on the session-start / upload-complete paths).
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

// ── Types ────────────────────────────────────────────────────────────────────

/** Optional per-section hint sent with the session create (the agent already
 *  carries the full prompt/sections; this is only timing context). */
export interface ElSessionSection {
    title: string;
    /** Section duration in seconds (from assessment `content.sections[].duration`). */
    duration: number;
}

export interface ElSessionRequest {
    conversation_id: string;
    assessment_id: string;
    candidate_name?: string;
    sections?: ElSessionSection[];
}

export interface ElS3UploadConfig {
    upload_id: string;
    s3_key: string;
}

export interface ElSessionData {
    el_signed_url: string;
    s3_upload_config: ElS3UploadConfig;
    /**
     * Candidate's parsed CV (minified/de-nulled/capped), sourced by the BE from S3.
     * ALWAYS a string — the literal `"No CV provided."` when there's no CV. The FE
     * forwards it verbatim as the `candidate_cv` dynamic variable at startSession so
     * the shared per-assessment agent can personalise its questions.
     */
    candidate_cv?: string;
}

/** One uploaded multipart part. `ETag` is kept verbatim (quoted) as S3 returns it. */
export interface ElUploadPart {
    PartNumber: number;
    ETag: string;
}

export interface ElCompleteUploadRequest {
    upload_id: string;
    s3_key: string;
    parts: ElUploadPart[];
}

// ── Endpoints ─────────────────────────────────────────────────────────────────

/**
 * POST /interviews/el/session — start the EL session + open the S3 multipart
 * upload. Returns the signed URL for the EL SDK and the upload config, or `null`
 * (e.g. a non-v2 assessment returns `code: 400` "Assessment not configured for
 * ElevenLabs (v2)").
 */
export const startElSession = async (
    payload: ElSessionRequest,
): Promise<ElSessionData | null> => {
    try {
        const res = await axios.post<StandardResponse<ElSessionData>>(
            `${apiURL}/interviews/el/session`,
            payload,
            { headers: authHeaders() },
        );
        return res.data?.data ?? null;
    } catch (error) {
        catchErrorLog(`startElSession failed: ${error}`);
        return null;
    }
};

/**
 * POST /interviews/el/el-conversation — tell the BE the EL conversation id
 * (needed for scoring + AIQM). Returns true on success.
 */
export const storeElConversation = async (
    conversation_id: string,
    el_conversation_id: string,
): Promise<boolean> => {
    try {
        const res = await axios.post<StandardResponse<{ status: string }>>(
            `${apiURL}/interviews/el/el-conversation`,
            { conversation_id, el_conversation_id },
            { headers: authHeaders() },
        );
        return (res.data?.code ?? 0) >= 200 && (res.data?.code ?? 0) < 300;
    } catch (error) {
        catchErrorLog(`storeElConversation failed: ${error}`);
        return false;
    }
};

/**
 * GET /interviews/el/s3-upload-part — get a presigned PUT URL for one multipart
 * chunk. Returns the URL string, or `null` on failure.
 */
export const getElS3UploadPart = async (params: {
    upload_id: string;
    part_number: number;
    s3_key: string;
}): Promise<string | null> => {
    try {
        const res = await axios.get<StandardResponse<{ presigned_url: string }>>(
            `${apiURL}/interviews/el/s3-upload-part`,
            { headers: authHeaders(), params },
        );
        return res.data?.data?.presigned_url ?? null;
    } catch (error) {
        catchErrorLog(`getElS3UploadPart failed: ${error}`);
        return null;
    }
};

/**
 * PUT one multipart chunk to its presigned S3 URL. Uses `fetch` (not axios) so no
 * Authorization / extra headers are attached — a presigned URL must be hit bare
 * or S3 rejects the signature. Returns the part's quoted `ETag` (read from the
 * response header — S3 CORS must expose it), or `null` on failure.
 */
export const putElUploadPart = async (
    presignedUrl: string,
    chunk: Blob,
): Promise<string | null> => {
    try {
        const res = await fetch(presignedUrl, { method: "PUT", body: chunk });
        if (!res.ok) {
            catchErrorLog(`putElUploadPart failed: HTTP ${res.status}`);
            return null;
        }
        return res.headers.get("ETag") || res.headers.get("etag") || null;
    } catch (error) {
        catchErrorLog(`putElUploadPart failed: ${error}`);
        return null;
    }
};

/**
 * POST /interviews/el/s3-complete — finalize the multipart upload with the
 * ordered part list. Returns the final `s3_key`, or `null` on failure.
 */
export const completeElS3Upload = async (
    payload: ElCompleteUploadRequest,
): Promise<string | null> => {
    try {
        const res = await axios.post<StandardResponse<{ s3_key: string }>>(
            `${apiURL}/interviews/el/s3-complete`,
            payload,
            { headers: authHeaders() },
        );
        return res.data?.data?.s3_key ?? null;
    } catch (error) {
        catchErrorLog(`completeElS3Upload failed: ${error}`);
        return null;
    }
};

/**
 * POST /interviews/el/recording-complete — kick off the BE transcript fetch +
 * scoring pipeline. `s3_key` is OPTIONAL: scoring runs off the stored EL
 * conversation id (the transcript), not the video, so this must be called on
 * every call end even when the upload failed — omit `s3_key` in that case (it
 * only controls whether the recorded video is playable later).
 *
 * Returns `{ ok, permanent }` rather than a bare boolean (#3460): a `404 "Video
 * not found"` means this conversation_id has no record, which no amount of
 * retrying will change, so the caller must stop instead of hammering it.
 */
export const markElRecordingComplete = async (
    conversation_id: string,
    s3_key?: string,
): Promise<{ ok: boolean; permanent: boolean }> => {
    try {
        const res = await axios.post<StandardResponse<{ status: string }>>(
            `${apiURL}/interviews/el/recording-complete`,
            { conversation_id, ...(s3_key ? { s3_key } : {}) },
            { headers: authHeaders() },
        );
        const code = res.data?.code ?? 0;
        return { ok: code >= 200 && code < 300, permanent: code >= 400 && code < 500 };
    } catch (error) {
        catchErrorLog(`markElRecordingComplete failed: ${error}`);
        return { ok: false, permanent: isClientError(error) };
    }
};
